"use client"

import * as React from "react"
import { Dialog as DialogPrimitive } from "@base-ui/react/dialog"
import { XIcon } from "@phosphor-icons/react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"

/**
 * Az asztali dialógus héja (Base UI `Dialog`). A shadcn alapon két saját döntés ül:
 * a jobb felső, kerek `secondary` bezáró gomb a fejléc `h-14` sorában (a cím
 * vonalában marad egy- és többsoros fejlécnél is), és a `DialogDescription`
 * alapból `sr-only` - a képernyőolvasó felolvassa, a látható magyarázat a hívóé.
 * Mobilon a `ResponsiveDialog` ehelyett a vaul `Sheet`-et rakja ki.
 */
function Dialog({ ...props }: DialogPrimitive.Root.Props) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />
}

function DialogTrigger({ ...props }: DialogPrimitive.Trigger.Props) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />
}

function DialogPortal({ ...props }: DialogPrimitive.Portal.Props) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />
}

function DialogClose({ ...props }: DialogPrimitive.Close.Props) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />
}

function DialogOverlay({
  className,
  forceRender = true,
  ...props
}: DialogPrimitive.Backdrop.Props) {
  return (
    <DialogPrimitive.Backdrop
      data-slot="dialog-overlay"
      forceRender={forceRender}
      // `pointer-events-auto`: ha ez a dialógus egy nyitott vaul Sheet (mobil
      // ResponsiveDialog) FÖLÖTT nyílik, a vaul `pointer-events: none`-t tart a
      // `body`-n; a body-ba portálozott háttér ezt örökölné, és a koppintás átesne
      // rajta a Sheet tartalmára. Az explicit `auto` visszaadja a kattinthatóságot.
      className={cn(
        "pointer-events-auto fixed inset-0 isolate z-50 bg-black/30 backdrop-blur-sm transition-opacity duration-150 data-starting-style:opacity-0 data-ending-style:opacity-0",
        className
      )}
      {...props}
    />
  )
}

function DialogContent({
  className,
  children,
  showCloseButton = true,
  ...props
}: DialogPrimitive.Popup.Props & {
  showCloseButton?: boolean
}) {
  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Popup
        data-slot="dialog-content"
        // `grid-cols-1` (= minmax(0,1fr)): az egyetlen rácsoszlop a konténer
        // szélességéhez igazodik, nem a tartalom max-content-jéhez - különben egy
        // hosszú, nem tördelhető tartalom keskeny nézetben kilógna a dialógusból.
        // `pointer-events-auto`: lásd a DialogOverlay megjegyzését.
        //
        // `will-change-transform`: a popup a nyitóanimáció (opacity+scale) idejére
        // saját kompozit réteget kap, majd Chrome elengedi - és a törzs ELSŐ
        // görgetése lépteti vissza. Az az egy újrarasztereződés a mögötte lévő
        // teljes képernyős `backdrop-filter` miatt az egész viewporton átvillan
        // (Chromium 339841685). A hinttel a réteg a dialógus teljes életére állandó,
        // így nincs mit visszaléptetni. Nem a háttérre tesszük, pedig az az elmosott
        // elem: a `backdrop-filter` amúgy is saját rétegen él, a `will-change`-dzsel
        // párosítva viszont WebKitben ismerten elveszhet az elmosás - a forgalom
        // java pedig iOS Safari. A `transform` itt a réteg-előléptetés kanonikus
        // jelzése; Blink a `translate` / `scale` longhandokat (amiket ez az elem
        // ténylegesen használ) ugyanide sorolja.
        className={cn(
          "pointer-events-auto fixed top-1/2 left-1/2 z-50 grid w-full max-w-[calc(100%-2rem)] grid-cols-1 -translate-x-1/2 -translate-y-1/2 gap-4 overflow-y-auto rounded-xl bg-popover p-4 text-sm text-popover-foreground ring-1 ring-foreground/10 outline-none max-h-[85vh] sm:max-w-lg will-change-transform transition-[opacity,scale] duration-200 data-starting-style:opacity-0 data-starting-style:scale-95 data-ending-style:opacity-0 data-ending-style:scale-95",
          className
        )}
        {...props}
      >
        {children}
        {showCloseButton && (
          // A bezáró gombot egy fix magasságú (h-14 = a fejléc min-h-14 sora) flex
          // konténer igazítja függőlegesen középre - így a címmel egy vonalban marad,
          // függetlenül a gomb méretétől és attól, hogy a fejléc egy- vagy többsoros.
          <div className="absolute top-0 right-4 flex h-14 items-center">
            <DialogPrimitive.Close
              data-slot="dialog-close"
              render={
                <Button
                  type="button"
                  variant="secondary"
                  size="icon"
                  className="rounded-full transition-transform hover:scale-110 active:scale-95"
                />
              }
            >
              <XIcon />
              <span className="sr-only">Bezárás</span>
            </DialogPrimitive.Close>
          </div>
        )}
      </DialogPrimitive.Popup>
    </DialogPortal>
  )
}

function DialogHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-header"
      className={cn("flex min-h-14 flex-col justify-center gap-2", className)}
      {...props}
    />
  )
}

function DialogFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        "-mx-4 -mb-4 flex flex-col-reverse gap-2 rounded-b-xl border-t bg-muted/50 p-4 sm:flex-row sm:justify-end",
        className
      )}
      {...props}
    />
  )
}

function DialogTitle({ className, ...props }: DialogPrimitive.Title.Props) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn("text-base leading-none font-medium", className)}
      {...props}
    />
  )
}

function DialogDescription({
  className,
  ...props
}: DialogPrimitive.Description.Props) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      // Látható alapból (natív shadcn); ahol csak képernyőolvasónak szól, a hívó ad
      // `sr-only`-t - a `ResponsiveDialog` a `descriptionVisible` propból dönti el.
      className={cn(
        "text-sm text-muted-foreground *:[a]:underline *:[a]:underline-offset-3 *:[a]:hover:text-foreground",
        className
      )}
      {...props}
    />
  )
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
}
